/**
 * Suggests titles for the automatic album from what the editor already knows about the photos.
 * No AI server involved, two helpers on the site (POST /app/album-titles) :
 *
 *  - dates + GPS : when the photos were taken in another country than the account's, the trip
 *    gives "Week-end à Londres" style titles from templates in the site language (city when known,
 *    country otherwise, a weekend / a week / longer, Christmas or summer). Place names come from
 *    action=geocode (Google reverse geocoding, cached server side), one call for all the GPS clusters.
 *  - otherwise (no GPS, or home country) a contact sheet of a dozen photos goes to action=suggest,
 *    where Claude proposes titles from the content.
 *
 * Generic titles from the dates alone ("Nos souvenirs de décembre 2024") are always available.
 *
 * A creation whose product has no theme (product.use_media == 0, or no product) is classified first :
 * up to three contact sheets go to action=suggest with classify=1 until Claude is confident about the
 * album category (travel, wedding, baby, love, animaux, family, birthday, party, cooking, else
 * classic ; all but party are theme_tags folders). The titles are then Claude's own ones, the
 * templates of that category (categoryTitles) and the generic ones, with a subtitle for the cover
 * (categorySubtitle : "Mardi 15 septembre 2026" for a wedding, "du 14 au 16 juin 2025" for a trip,
 * "© 2026" for recipes...). The editor loads the themes of that tag (BookRepository.applyThemeTag)
 * so the layouts panel offers them, and the creator draws the cover of that category
 * (covers.CoverDesigns).
 *
 * suggest(photos) answers { local : [titles], remote : Promise<[titles]>, category : Promise, context } :
 * local is immediate, remote resolves with the full ordered list and never rejects ; category
 * resolves with { folder, themetag, subtitle, confidence, titles, covers, country } (null for a themed
 * product ; covers = coverSignals, what the proposed covers are chosen on ; country = singleCountry,
 * the ISO code of the one country the photos were taken in). The list
 * opens on both sources, PROPOSALS_PER_SOURCE titles of Claude's then as many templates (_balance),
 * so the title screen never fills its four lines with one source.
 */
fx.service(class TitleSuggester extends fx.Service
{
    constructor(model)
    {
        super();

        this.model = model.creationModel;
        this.endpoint = "/app/album-titles";

        this.MAX_POINTS = 3;        // GPS clusters geocoded
        this.SHEET_PHOTOS = 12;     // photos on the contact sheet
        this.SHEET_COLUMNS = 4;
        this.SHEET_CELL = { width : 320, height : 240 };   // 12 cells : a 1280 x 720 sheet, faces and dresses stay readable

        this.PROPOSALS_PER_SOURCE = 2;   // titles of each source opening the list, Claude's then the templates (the title screen shows four)

        this.CLASSIFY_SHEETS = 3;        // contact sheets sent at most to recognise the category
        this.CLASSIFY_CONFIDENT = 0.7;   // Claude's confidence that ends the classification
        this.CLASSIFY_MINIMUM = 0.4;     // below, the category is not trusted : "classic"

        this.COVER_SHEETS = 3;           // numbered sheets sent at most to find the cover photo
        this.COVER_CONFIDENT = 0.7;      // a cell that good ends the search
        this.COVER_MINIMUM = 0.35;       // below, the curator's own choice stands
        this.COVER_BUDGET = 45000;       // the whole search, sheets included : the placement waits on it

        // album categories : theme_tags.folder keys, "party" apart (no theme of its own)
        this.CATEGORIES = ["travel", "wedding", "baby", "love", "animaux", "family", "birthday", "party", "cooking", "classic"];
        this.DEFAULT_CATEGORY = "classic";
    }

    /** The category is looked for when the product carries no theme (use_media 0) or is unknown. */
    get classifies()
    {
        let creation = this.model ? this.model.creation : null;
        let product = creation ? creation.product : null;

        return !(product && Number(product.use_media));
    }

    get lang()
    {
        let lang = (typeof fx.currentLang == "function") ? fx.currentLang() : "";
        return ["fr", "en", "es", "de", "it"].indexOf(lang) != -1 ? lang : "fr";
    }

    get locale()
    {
        return { fr : "fr-FR", en : "en-GB", es : "es-ES", de : "de-DE", it : "it-IT" }[this.lang];
    }

    /** ISO code of the account country, the site language's country when the account has none. */
    get accountCountry()
    {
        let account = this.model ? this.model.account : null;
        let code = account ? (account.countrycode || account.isocode) : "";

        if (code)
            return String(code).toUpperCase();

        return { fr : "FR", en : "GB", es : "ES", de : "DE", it : "IT" }[this.lang];
    }

    suggest(photos)
    {
        let list = ((photos && photos.toArray) ? photos.toArray() : (photos || []).slice()).filter((photo) => photo && !photo.deleted);

        // the same photos again (back and forth between the screens) : the same answer, no new request
        let key = this._photosKey(list);
        if (this._memo && this._memo.key == key)
            return this._memo.result;

        let context = this.context(list);
        let local = this.genericTitles(context);
        let category, remote;

        let places = this._geocode(context.points).then((found) => {
            context.places = found;
            // the place makes the titles only for an album about one place (aboutOnePlace)
            context.place = this.aboutOnePlace(context, found) ? this.foreignPlace(found) : null;
            this._debug("places", { accountCountry : this.accountCountry, places : found, place : context.place });
        });

        if (this.classifies)
        {
            // no theme on the product : Claude tells the category, the titles follow it
            category = places
                .then(() => this._classify(list, context))
                .catch((e) => { this._debug("error", e); return null; })
                .then((answer) => {
                    let folder = (answer && answer.category && answer.confidence >= this.CLASSIFY_MINIMUM) ? answer.category : this.DEFAULT_CATEGORY;
                    context.category = folder;

                    let info = {
                        folder : folder,
                        themetag : this._themeTag(folder),
                        subtitle : this.categorySubtitle(context, folder),
                        confidence : answer ? answer.confidence : 0,
                        titles : (answer && answer.titles) ? answer.titles : [],
                        covers : this.coverSignals(context, folder),
                        country : this.singleCountry(context.places)
                    };
                    this._debug("category", info);
                    return info;
                });

            remote = category.then((info) => {
                // the titles Claude wrote for these very pictures and the templates of the category,
                // both on the screen, the generic ones closing the list
                let all = this._balance(info.titles, this.categoryTitles(context, info.folder), local);
                this._debug("titles", { category : info.folder, remote : info.titles, all : all });
                return all;
            });
        }
        else
        {
            category = Promise.resolve(null);

            remote = places
                .then(() => {
                    if (context.place)
                        return this.placeTitles(context);

                    return this._contentTitles(list, context);
                })
                .catch((e) => { this._debug("error", e); return []; })
                .then((titles) => {
                    // a trip answers place templates, written for this very trip and specific enough
                    // to fill the screen on their own ; otherwise Claude wrote these titles and the
                    // generic ones from the dates are the templates sharing the screen with them
                    let all = context.place ? this._dedupe(titles.concat(local)) : this._balance(titles, local, []);
                    this._debug("titles", { remote : titles, all : all });
                    return all;
                });
        }

        this._debug("context", { photos : list.length, located : list.filter((p) => Number(p.latitude) && Number(p.longitude)).length, classify : this.classifies, context : context, local : local });

        let result = { local : local, remote : remote, category : category, context : context };
        this._memo = { key : key, result : result };

        return result;
    }

    /**
     * What identifies a set of photos for the suggestions : their dates, positions and sizes, not
     * their ids (a photo gets its server id once uploaded, the titles do not change for that).
     */
    _photosKey(photos)
    {
        return photos.length + ":" + photos
            .map((photo) => [curator.MomentClustering.time(photo) || 0, photo.latitude || "", photo.longitude || "", photo.width || 0, photo.height || 0].join(","))
            .sort()
            .join("|");
    }

    // ------------------------------ context ------------------------------ //

    /**
     * What the dates and positions say : { count, start, end, spanDays, weekend, week, long,
     * christmas, summer, monthLabel, year, points : [{ lat, lng, count }] }
     */
    context(photos)
    {
        let times = photos.map((photo) => curator.MomentClustering.time(photo)).filter((t) => t > 0).sort((a, b) => a - b);
        let context = { count : photos.length, start : 0, end : 0, spanDays : 0, weekend : false, week : false, long : false, christmas : false, summer : false, monthLabel : "", year : 0, endYear : 0, startMonth : 0, endMonth : 0, christmasYear : 0, mainDay : 0, mainDayKey : "", points : [] };

        if (times.length)
        {
            context.start = times[0];
            context.end = times[times.length - 1];

            let first = new Date(context.start * 1000), last = new Date(context.end * 1000);
            let dayStart = new Date(first.getFullYear(), first.getMonth(), first.getDate());
            let dayEnd = new Date(last.getFullYear(), last.getMonth(), last.getDate());
            context.spanDays = Math.round((dayEnd - dayStart) / 86400000) + 1;

            let startDay = first.getDay();   // 0 sunday ... 6 saturday
            context.weekend = context.spanDays >= 2 && context.spanDays <= 4 && (startDay == 5 || startDay == 6 || startDay == 0);
            context.week = context.spanDays >= 5 && context.spanDays <= 10;
            context.long = context.spanDays > 10;

            // Christmas, summer : when most of the photos were taken then, not merely when the dates
            // touch the season (three years of photos starting in November are no Christmas album)
            let christmas = 0, summer = 0;
            for (let i = 0; i < times.length; i++)
            {
                let d = new Date(times[i] * 1000), month = d.getMonth(), day = d.getDate();
                if ((month == 11 && day >= 20) || (month == 0 && day <= 3))
                    christmas++;
                if (month == 6 || month == 7)
                    summer++;
            }
            context.christmas = christmas * 2 >= times.length;
            context.summer = summer * 2 >= times.length;

            context.monthLabel = this._capitalize(first.toLocaleDateString(this.locale, { month : "long", year : "numeric" }));
            context.year = first.getFullYear();
            context.endYear = last.getFullYear();
            context.startMonth = first.getMonth();
            context.endMonth = last.getMonth();

            // the day most photographed : the day of the event for a wedding or a family celebration
            let days = {};
            for (let i = 0; i < times.length; i++)
            {
                let d = new Date(times[i] * 1000);
                let key = d.getFullYear() + "-" + d.getMonth() + "-" + d.getDate();
                days[key] = (days[key] || 0) + 1;
                if (!context.mainDay || days[key] > days[context.mainDayKey])
                {
                    context.mainDay = times[i];
                    context.mainDayKey = key;
                }
            }
            // photos of early January belong to the Christmas of the previous year
            context.christmasYear = (first.getMonth() == 0) ? first.getFullYear() - 1 : first.getFullYear();
        }

        // GPS clusters : positions rounded to 0.05 degree (about 5 km), most photographed first
        let clusters = {};
        for (let i = 0; i < photos.length; i++)
        {
            let lat = Number(photos[i].latitude), lng = Number(photos[i].longitude);
            if (!lat || !lng || isNaN(lat) || isNaN(lng))
                continue;

            let key = (Math.round(lat * 20) / 20).toFixed(2) + "," + (Math.round(lng * 20) / 20).toFixed(2);
            if (!clusters[key])
                clusters[key] = { lat : 0, lng : 0, count : 0 };

            clusters[key].lat += lat;
            clusters[key].lng += lng;
            clusters[key].count++;
        }

        context.points = Object.keys(clusters)
            .map((key) => ({ lat : clusters[key].lat / clusters[key].count, lng : clusters[key].lng / clusters[key].count, count : clusters[key].count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, this.MAX_POINTS);

        return context;
    }

    /**
     * The trip destination when most located photos were taken outside the account's country :
     * { name, kind : "city" | "country", country, countryCode }, null otherwise.
     */
    foreignPlace(places)
    {
        let home = this.accountCountry;
        let located = (places || []).filter((place) => place && place.countryCode);
        if (!located.length)
            return null;

        let total = 0, abroad = 0, best = null;
        for (let i = 0; i < located.length; i++)
        {
            let place = located[i], count = place.count || 1;
            total += count;

            if (place.countryCode.toUpperCase() != home)
            {
                abroad += count;
                if (!best || count > (best.count || 1))
                    best = place;
            }
        }

        if (!best || abroad * 2 < total)
            return null;

        let name = best.city || best.region || best.country;
        return { name : name, kind : best.city || best.region ? "city" : "country", country : best.country, countryCode : best.countryCode.toUpperCase() };
    }

    /**
     * ISO code of the one country every located photo was taken in (the trip covers of
     * covers.CoverDesigns.countries), "" when the photos carry no position or span several.
     */
    singleCountry(places)
    {
        let codes = (places || [])
            .filter((place) => place && place.countryCode)
            .map((place) => String(place.countryCode).toUpperCase());

        return (codes.length && codes.every((code) => code == codes[0])) ? codes[0] : "";
    }

    // ------------------------------ titles ------------------------------- //

    placeTitles(context)
    {
        let place = context.place;
        if (!place || !place.name)
            return [];

        let city = place.kind == "city";
        let keys = [];

        if (context.christmas)
            keys.push(city ? "Les vacances de Noël à $" : "Noël : $", city ? "La fin d'année à $" : "Les fêtes : $");

        if (context.weekend)
            keys.push(city ? "Week-end à $" : "Notre week-end : $", city ? "Un saut à $" : "Souvenirs : $", city ? "Escapade à $" : "Découverte : $");
        else if (context.week)
            keys.push(city ? "Une semaine à $" : "Nos vacances : $", city ? "À la découverte de $" : "Découverte : $", city ? "Nos vacances à $" : "Souvenirs : $");
        else if (context.long)
            keys.push(city ? "À la découverte de $" : "Découverte : $", city ? "Nos vacances à $" : "Nos vacances : $", city ? "Un voyage à $" : "Souvenirs : $");
        else
            keys.push(city ? "Une journée à $" : "Souvenirs : $", city ? "Escapade à $" : "Découverte : $");

        if (context.summer)
            keys.push(city ? "Un été à $" : "Un été : $");

        return this._dedupe(keys.map((key) => fx.text(key, place.name)));
    }

    genericTitles(context)
    {
        let titles = [];

        if (context.christmas)
            titles.push(fx.text("Noël $", context.christmasYear), fx.text("Les fêtes de fin d'année"));

        if (context.weekend)
            titles.push(fx.text("Notre week-end"), fx.text("Un week-end en famille"));
        else if (context.week || context.long)
            titles.push(fx.text("Nos vacances"), fx.text("Souvenirs de vacances"));

        if (context.summer)
            titles.push(fx.text("L'été $", context.year));

        if (context.monthLabel)
            titles.push(fx.text("Nos souvenirs de $", context.monthLabel), context.monthLabel);

        titles.push(fx.text("Nos plus beaux moments"));

        return this._dedupe(titles);
    }

    // ---------------------------- categories ----------------------------- //

    /** models.ThemeTag of a folder, when the editor knows the tags. */
    _themeTag(folder)
    {
        let tags = this.model ? this.model.themetags : null;

        return (tags && tags.find) ? (tags.find({ folder : folder }) || null) : null;
    }

    /** The place most photographed, wherever it is : { name, kind } or null. */
    _bestPlace(places)
    {
        let located = (places || []).filter((place) => place && place.countryCode);
        if (!located.length)
            return null;

        let best = located.reduce((a, b) => ((b.count || 1) > (a.count || 1) ? b : a));
        let name = best.city || best.region || best.country;

        return { name : name, kind : (best.city || best.region) ? "city" : "country", country : best.country, countryCode : best.countryCode };
    }

    /** Title templates of an album category, in the site language, the place ones first for a trip. */
    categoryTitles(context, folder)
    {
        let titles = [];

        switch (folder)
        {
            case "wedding":
                titles.push(fx.text("Notre Mariage"), fx.text("Notre plus beau jour"), fx.text("Oui pour la vie"));
                break;

            case "travel":
            {
                let place = context.place || this._bestPlace(context.places);
                if (place)
                    titles.push.apply(titles, this.placeTitles(Object.assign({}, context, { place : place })));
                if (context.summer)
                    titles.push(fx.text("L'été $", context.year));
                titles.push(fx.text("Nos vacances"), fx.text("Carnet de voyage"), fx.text("Souvenirs de voyage"));
                break;
            }

            case "baby":
                titles.push(fx.text("Notre petit ange"), fx.text("Bienvenue au monde"), fx.text("Nos premiers moments"));
                break;

            case "love":
                titles.push(fx.text("Notre histoire"), fx.text("Toi et moi"), fx.text("Nous deux"));
                break;

            case "animaux":
                titles.push(fx.text("Mon animal"), fx.text("Mon compagnon"), fx.text("Nos compagnons"));
                break;

            case "family":
                titles.push(fx.text("Fête de famille"), fx.text("Réunion de famille"), fx.text("Moment de famille"));
                break;

            case "birthday":
                titles.push(fx.text("Joyeux anniversaire"), fx.text("Mon anniversaire"), fx.text("Une année de plus"));
                break;

            case "party":
                titles.push(fx.text("Notre soirée"), fx.text("Entre amis"), fx.text("C'était la fête"));
                break;

            case "cooking":
                titles.push(fx.text("Mes recettes"), fx.text("Nos recettes de famille"), fx.text("Le livre de recettes"));
                break;

            default:
                // photos mostly taken abroad : the place titles stay, whatever Claude answered (or failed to)
                if (context.place)
                    titles.push.apply(titles, this.placeTitles(context));
                titles.push(fx.text("Nos bons moments"));
                if (context.weekend)
                    titles.push(fx.text("Un week-end en images"));
                if (context.christmas || context.endMonth == 11)
                    titles.push(fx.text("Une fin d'année réunis"));
                break;
        }

        return this._dedupe(titles);
    }

    /**
     * The cover subtitle of a category : a day, a period or a copyright line ; "" without dates.
     * Photos spread over more than a year give the first and last years ("2024 - 2026"), whatever
     * the category.
     */
    categorySubtitle(context, folder)
    {
        if (!context.start)
            return (folder == "cooking") ? fx.text("© $", new Date().getFullYear()) : "";

        if (this._spansYears(context))
            return this._yearsLabel(context);

        switch (folder)
        {
            case "wedding":
                return this._fullDate(context.mainDay || context.start);

            case "family":
            case "birthday":
                return this._dayDate(context.mainDay || context.start);

            case "party":
                return this._rangeLabel(context);

            case "cooking":
                return fx.text("© $", context.endYear || context.year);

            case "travel":
            case "classic":
                return this._rangeLabel(context);

            default:
                return this._yearLabel(context);
        }
    }

    /** More than a year between the first and the last photo (or two different years far apart). */
    _spansYears(context)
    {
        return context.endYear != context.year && (context.spanDays > 365 || context.endYear - context.year > 1);
    }

    /** "2024 - 2026" */
    _yearsLabel(context)
    {
        return context.year + " - " + context.endYear;
    }

    /** "Mardi 15 septembre 2026" */
    _fullDate(time)
    {
        return this._capitalize(new Date(time * 1000).toLocaleDateString(this.locale, { weekday : "long", day : "numeric", month : "long", year : "numeric" }));
    }

    /** "15 septembre 2026" */
    _dayDate(time)
    {
        return new Date(time * 1000).toLocaleDateString(this.locale, { day : "numeric", month : "long", year : "numeric" });
    }

    /** "15 septembre 2026", "du 14 au 16 juin 2025", "du 28 juin au 2 juillet 2025", "Mai 2026", "2025 – 2026" */
    _rangeLabel(context)
    {
        let first = new Date(context.start * 1000), last = new Date(context.end * 1000);

        if (first.getFullYear() != last.getFullYear())
            return first.getFullYear() + " - " + last.getFullYear();

        if (context.spanDays <= 1)
            return this._dayDate(context.start);

        if (context.spanDays > 20)
            return this._yearLabel(context);

        if (first.getMonth() == last.getMonth())
            return fx.text("du $ au $ $", first.getDate(), last.getDate(), last.toLocaleDateString(this.locale, { month : "long", year : "numeric" }));

        return fx.text("du $ au $", first.toLocaleDateString(this.locale, { day : "numeric", month : "long" }), last.toLocaleDateString(this.locale, { day : "numeric", month : "long", year : "numeric" }));
    }

    /** "Septembre 2026", "Début 2026", "Été 2026", "Fin 2026", "Année 2026", "2025 – 2026" */
    /**
     * The period the capture dates of the photos cover, for the cover subtitle : one month
     * ("Septembre 2026"), the start, summer or end of a year ("Début 2026", "Été 2026", "Fin 2026"),
     * a whole year ("Année 2026") or the years ("2025 - 2026"). Empty when no photo carries a date.
     */
    periodLabel(photos)
    {
        let context = this.context(photos || []);

        return context.start ? this._yearLabel(context) : "";
    }

    _yearLabel(context)
    {
        if (context.year != context.endYear)
            return context.year + " - " + context.endYear;

        let from = context.startMonth, to = context.endMonth;
        if (from == to)
            return context.monthLabel;
        if (to <= 3)
            return fx.text("Début $", context.year);
        if (from >= 5 && to <= 8)
            return fx.text("Été $", context.year);
        if (from >= 8)
            return fx.text("Fin $", context.year);

        return fx.text("Année $", context.year);
    }

    /**
     * What the covers are chosen on (BookRepository.applyAlbumCategory, CoverProposer on the
     * server) : { category, points : [{ lat, lng }] }. The covers naming where the photos were taken
     * come first, then those of the category, nothing else.
     */
    coverSignals(context, folder)
    {
        return { category : folder, points : this.coverPoints(context) };
    }

    /**
     * The positions the covers may be chosen on (context.points, most photographed first) : none
     * when the photos span more than COVER_PLACE_MAX_MONTHS — a year of memories is not about one
     * place, and the covers then follow the category only. The server drops the place as well when
     * the positions fall in more than one country (LayoutsController::placesOf).
     */
    coverPoints(context)
    {
        if (!context || !context.points || !context.points.length || !this.withinPlaceSpan(context))
            return [];

        return context.points.map((point) => ({ lat : point.lat, lng : point.lng }));
    }

    /** The photos span COVER_PLACE_MAX_MONTHS at most (undated : true). */
    withinPlaceSpan(context)
    {
        if (!context.start || !context.end)
            return true;

        let limit = new Date(context.start * 1000);
        limit.setMonth(limit.getMonth() + this.COVER_PLACE_MAX_MONTHS);

        return context.end * 1000 <= limit.getTime();
    }

    /**
     * Whether an album is about one place, so that its titles may name it : its photos span
     * COVER_PLACE_MAX_MONTHS at most, and their located clusters all fall in one country. Three
     * years of a couple's life with a few days in Braga is not "Nos vacances à Braga".
     */
    aboutOnePlace(context, places)
    {
        if (!this.withinPlaceSpan(context))
            return false;

        let countries = {};
        (places || []).forEach((place) => { if (place && place.countryCode) countries[String(place.countryCode).toUpperCase()] = true; });

        return Object.keys(countries).length <= 1;
    }

    get COVER_PLACE_MAX_MONTHS()
    {
        return 3;
    }

    // ------------------------------ server -------------------------------- //

    /** One request for every cluster : the server answers { places : [...] } in the same order. */
    _geocode(points)
    {
        if (!points || !points.length)
            return Promise.resolve([]);

        let payload = {
            action : "geocode",
            lang : this.lang,
            points : JSON.stringify(points.map((point) => ({ lat : Number(point.lat.toFixed(4)), lng : Number(point.lng.toFixed(4)) })))
        };

        return this._request(payload, 15000).then((result) => {
            let places = (result && result.places) ? result.places : [];
            let located = [];

            for (let i = 0; i < points.length; i++)
            {
                let place = places[i];
                if (place && place.countryCode)
                    located.push(Object.assign({ count : points[i].count }, place));
            }

            return located;
        });
    }

    _suggestPayload(image, context, classify)
    {
        let known = (context.places && context.places.length) ? context.places[0] : {};

        return {
            action : "suggest",
            image : image,
            lang : this.lang,
            classify : classify ? 1 : 0,
            context : JSON.stringify({
                days : context.spanDays,
                start : context.start ? new Date(context.start * 1000).toISOString().slice(0, 10) : null,
                end : context.end ? new Date(context.end * 1000).toISOString().slice(0, 10) : null,
                count : context.count,
                city : known.city || null,
                region : known.region || null,
                country : known.country || null,
                weekend : context.weekend,
                season : context.christmas ? "christmas" : (context.summer ? "summer" : null)
            })
        };
    }

    _contentTitles(photos, context)
    {
        if (!photos.length)
            return Promise.resolve([]);

        return this._contactSheet(photos).then((image) => {
            if (!image)
                return [];

            return this._request(this._suggestPayload(image, context, false), 60000)
                .then((result) => (result && result.titles) ? result.titles.filter((t) => typeof t == "string" && t.trim()) : []);
        });
    }

    /**
     * The album category from Claude : a contact sheet, then another one or two with other photos
     * while the answer is not confident enough. Answers { category, confidence, titles } (the titles
     * of the answers agreeing with the category), or null when nothing came back.
     */
    _classify(photos, context)
    {
        if (!photos.length)
            return Promise.resolve(null);

        // other photos on every sheet : only as many rounds as the album has distinct dozens
        let rounds = Math.max(1, Math.min(this.CLASSIFY_SHEETS, Math.floor(photos.length / this.SHEET_PHOTOS)));
        let answers = [];

        let round = (index) => this._contactSheet(photos, index)
            .then((image) => image ? this._request(this._suggestPayload(image, context, true), 60000) : null)
            .then((result) => {
                if (result && result.category)
                    answers.push({ category : result.category, confidence : Number(result.confidence) || 0, titles : (result.titles || []).filter((t) => typeof t == "string" && t.trim()) });

                this._debug("classify", { round : index + 1, answer : result });

                let best = this._bestAnswer(answers);
                if (best && best.confidence >= this.CLASSIFY_CONFIDENT)
                    return best;

                // the server could not reach Claude and says why : the next sheets would fail the
                // same way, each one a full picture uploaded for nothing
                if (result && result.reason)
                    return best;

                if (index + 1 < rounds)
                    return round(index + 1);

                return best;
            });

        return round(0);
    }

    // ------------------------------ cover -------------------------------- //

    /**
     * The cover photo of a category that needs a precise subject on it : a wedding album whose
     * cover shows the reception hall rather than the bride and groom is a miss, and neither the
     * curation score nor the number of faces tells who is who.
     *
     * The best candidates (_coverCandidates) are sent to Claude as numbered contact sheets, one at
     * a time and up to COVER_SHEETS of them, until a cell comes back confident enough or COVER_BUDGET
     * is spent ; what the category wants to see is described server side (config/albumtitles.php
     * `cover_subjects`).
     * Answers that photo, or null when no sheet holds it — the creator then keeps the photo the
     * curator chose. Never rejects.
     */
    coverPhoto(photos, category)
    {
        let list = ((photos && photos.toArray) ? photos.toArray() : (photos || []).slice()).filter((photo) => photo && !photo.deleted);

        if (!list.length || !category)
            return Promise.resolve(null);

        let candidates = this._coverCandidates(list);
        let best = null;

        // the placement waits on this search with its progress screen already full : it is given one
        // budget for all its rounds, each request taking what is left of it
        let deadline = Date.now() + this.COVER_BUDGET;
        let left = () => deadline - Date.now();

        let answer = () => (best && best.confidence >= this.COVER_MINIMUM) ? best.photo : null;

        let round = (index) => {
            let sample = candidates.slice(index * this.SHEET_PHOTOS, (index + 1) * this.SHEET_PHOTOS);

            if (!sample.length || left() <= 0)
                return Promise.resolve(answer());

            return this._sheet(sample, true)
                .then((sheet) => {
                    if (!sheet)
                        return null;

                    let payload = { action : "cover", image : sheet.image, category : category, lang : this.lang, count : sheet.photos.length };

                    // never less than a few seconds : a request born with no time left is a sheet
                    // built and uploaded for an answer nobody waits for
                    return this._request(payload, Math.max(8000, left())).then((result) => ({ sheet : sheet, result : result }));
                })
                .then((found) => {
                    let result = found ? found.result : null;
                    let cell = result ? Math.round(Number(result.index)) : 0;
                    let confidence = result ? (Number(result.confidence) || 0) : 0;
                    let photo = (found && cell >= 1 && cell <= found.sheet.photos.length) ? found.sheet.photos[cell - 1] : null;

                    this._debug("cover", { round : index + 1, category : category, cell : cell, confidence : confidence, shows : result ? result.shows : "", reason : result ? result.reason : "no answer" });

                    if (photo && (!best || confidence > best.confidence))
                        best = { photo : photo, confidence : confidence };

                    if (best && best.confidence >= this.COVER_CONFIDENT)
                        return best.photo;

                    // the server could not reach Claude and says why : the next sheets would fail
                    // the same way, each one a full picture uploaded for nothing
                    if (result && result.reason)
                        return answer();

                    if (index + 1 < this.COVER_SHEETS && left() > 0)
                        return round(index + 1);

                    return answer();
                });
        };

        return round(0).catch((e) => { this._debug("error", e); return null; });
    }

    /**
     * The photos worth showing to Claude for the cover, best first : the curation score, raised for
     * the pictures holding one or two faces — what such a cover needs is people, and a couple comes
     * as two. Cut to what COVER_SHEETS sheets can carry.
     */
    _coverCandidates(photos)
    {
        let rank = (photo) => {
            let score = (photo.curation && photo.curation.score !== undefined) ? photo.curation.score : 0.5;
            let faces = this._faceCount(photo);

            return score + (faces == 2 ? 0.30 : (faces == 1 || faces == 3 ? 0.12 : (faces > 3 ? 0.04 : 0)));
        };

        return photos.slice().sort((a, b) => rank(b) - rank(a)).slice(0, this.SHEET_PHOTOS * this.COVER_SHEETS);
    }

    /** Faces of a photo : the ones the curation just found, else the ones stored with the photo. */
    _faceCount(photo)
    {
        let service = fx.service("PhotoCurator");
        let found = (service && service.facesOf) ? service.facesOf(photo) : null;

        if (found && found.length)
            return found.length;

        return photo.facesPositions ? photo.facesPositions.length : 0;
    }

    /** The category the answers agree on the most : confidences added up, capped to 1. */
    _bestAnswer(answers)
    {
        if (!answers.length)
            return null;

        let scores = {};
        for (let i = 0; i < answers.length; i++)
        {
            let answer = answers[i];
            if (!scores[answer.category])
                scores[answer.category] = { category : answer.category, confidence : 0, titles : [] };
            scores[answer.category].confidence += answer.confidence;
            scores[answer.category].titles = scores[answer.category].titles.concat(answer.titles);
        }

        let best = Object.keys(scores).map((key) => scores[key]).sort((a, b) => b.confidence - a.confidence)[0];
        best.confidence = Math.min(1, best.confidence);

        return best;
    }

    /**
     * A grid of thumbnails sampled evenly along the album, as a jpeg data url (null when nothing
     * loads). round (0, 1, 2...) shifts the sample so that every sheet shows other photos.
     */
    _contactSheet(photos, round)
    {
        let sorted = curator.MomentClustering.sort(photos);
        let count = Math.min(this.SHEET_PHOTOS, sorted.length);
        let shift = round || 0;
        let sample = [];
        for (let i = 0; i < count; i++)
            sample.push(sorted[(Math.floor(i * sorted.length / count) + shift) % sorted.length]);

        return this._sheet(sample).then((sheet) => sheet ? sheet.image : null);
    }

    /**
     * The given photos drawn in a grid, as a jpeg data url : { image, photos } where photos are the
     * ones that made it onto the sheet, in cell order (a picture that fails to load is left out).
     * `numbered` writes the cell number in the corner of each one, so that Claude can name it.
     * Answers null when nothing loads.
     */
    _sheet(sample, numbered)
    {
        let analyzer = new curator.ImageAnalyzer({ size : Math.max(this.SHEET_CELL.width, this.SHEET_CELL.height) });

        return Promise.all(sample.map((photo) => analyzer.load(photo).catch(() => null))).then((canvases) => {
            let cells = [];
            for (let i = 0; i < canvases.length; i++)
                if (canvases[i])
                    cells.push({ canvas : canvases[i], photo : sample[i] });

            if (!cells.length)
                return null;

            let columns = Math.min(this.SHEET_COLUMNS, cells.length);
            let rows = Math.ceil(cells.length / columns);
            let cell = this.SHEET_CELL;

            let sheet = document.createElement("canvas");
            sheet.width = columns * cell.width;
            sheet.height = rows * cell.height;

            let ctx = sheet.getContext("2d");
            ctx.fillStyle = "#ffffff";
            ctx.fillRect(0, 0, sheet.width, sheet.height);

            for (let i = 0; i < cells.length; i++)
            {
                let source = cells[i].canvas;
                let scale = Math.min(cell.width / source.width, cell.height / source.height);
                let w = source.width * scale, h = source.height * scale;
                let left = (i % columns) * cell.width, top = Math.floor(i / columns) * cell.height;
                ctx.drawImage(source, left + (cell.width - w) / 2, top + (cell.height - h) / 2, w, h);

                if (numbered)
                    this._drawNumber(ctx, i + 1, left, top);
            }

            try {
                return { image : sheet.toDataURL("image/jpeg", 0.72), photos : cells.map((entry) => entry.photo) };
            }
            catch (e) {
                return null;
            }
        });
    }

    /** The cell number, white on a black tab in the top left corner, readable over any picture. */
    _drawNumber(ctx, number, left, top)
    {
        let size = 34;

        ctx.save();
        ctx.fillStyle = "rgba(0, 0, 0, 0.75)";
        ctx.fillRect(left, top, size + 12, size + 4);
        ctx.fillStyle = "#ffffff";
        ctx.font = "bold " + size + "px sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(String(number), left + (size + 12) / 2, top + (size + 4) / 2);
        ctx.restore();
    }

    _request(data, timeout)
    {
        return new Promise((resolve) => {
            try {
                this.post(this.endpoint, data, (result) => resolve((result && !result.error) ? result : null), { timeout : timeout || 20000, maxTries : 1 });
            }
            catch (e) {
                resolve(null);
            }
        });
    }

    // ------------------------------ helpers ------------------------------- //

    /** Console trace of the whole suggestion, enabled with ?debug-titles=1 on the editor url. */
    _debug(step, data)
    {
        if (typeof Utils == "undefined" || !Utils.getURLParam("debug-titles"))
            return;

        try { console.log("[titles] " + step, JSON.parse(JSON.stringify(data))); }
        catch (e) { console.log("[titles] " + step, data); }
    }

    /**
     * The proposals with both sources represented : PROPOSALS_PER_SOURCE titles of Claude's, then as
     * many templates, so the title screen shows some of each. What is left of the two lists follows,
     * `others` closing it. A source shorter than its quota leaves its slots to the other one.
     */
    _balance(claude, templates, others)
    {
        claude = claude || [];
        templates = templates || [];

        let quota = this.PROPOSALS_PER_SOURCE;
        let opening = this._dedupe(claude).slice(0, quota);
        let taken = {};
        opening.forEach((title) => { taken[title.toLowerCase()] = true; });

        // a template Claude wrote too is already there : the next one takes the slot
        let seconds = this._dedupe(templates).filter((title) => !taken[title.toLowerCase()]).slice(0, quota);

        return this._dedupe(opening.concat(seconds, claude, templates, others || []));
    }

    _dedupe(titles)
    {
        let seen = {}, result = [];
        for (let i = 0; i < titles.length; i++)
        {
            let title = String(titles[i] || "").trim();
            let key = title.toLowerCase();
            if (!title || seen[key])
                continue;
            seen[key] = true;
            result.push(title);
        }
        return result;
    }

    _capitalize(text)
    {
        return text ? text.charAt(0).toUpperCase() + text.slice(1) : "";
    }
});
